import React, { useState, useEffect, useCallback, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, Spinner, Alert, Button, Table } from "react-bootstrap";
import axios from "axios";
import LabelFieldComponent from "../../../../components/fields/LabelFieldComponent";
import PageLayout from "../../../../layouts/PageLayout";

export default function ListerIndex() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [listers, setListers] = useState([]);
    const [search, setSearch] = useState("");

    const fetchListers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/lister`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );
            if (response.data?.success) {
                setListers(response.data.data || []);
            } else {
                setError(response.data?.message || "Failed to fetch listers.");
            }
        } catch (err) {
            console.error("Error fetching listers:", err);
            setError(err.response?.data?.message || "Failed to fetch listers list.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchListers();
    }, [fetchListers]);

    const filteredListers = listers.filter((l) => {
        const q = search.toLowerCase();
        return (
            l.name?.toLowerCase().includes(q) ||
            l.email?.toLowerCase().includes(q) ||
            l.nickname?.toLowerCase().includes(q)
        );
    });

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">
                                    {t("Listers")} ({filteredListers.length})
                                </h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">
                                            {t("Home")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">{t("Listers")}</li>
                                </ul>
                            </div>
                            <Button
                                variant="primary"
                                onClick={() => navigate("/listers/create")}
                            >
                                <i className="material-icons me-1 vertical-middle">add</i>
                                {t("Add Lister")}
                            </Button>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card p-4 rounded-4 shadow-sm border-0 bg-white">
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError("")} className="mb-4">
                                {error}
                            </Alert>
                        )}

                        <Row className="g-3 align-items-end mb-4">
                            <Col md={6} xl={4}>
                                <LabelFieldComponent
                                    type="search"
                                    placeholder="Search by Name, Email or Nickname..."
                                    label="Search"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </Col>
                        </Row>

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted">{t("Loading listers...")}</p>
                            </div>
                        ) : (
                            <div className="mc-table-responsive">
                                <Table hover className="mc-table align-middle">
                                    <thead className="mc-table-head primary">
                                        <tr>
                                            <th>Name</th>
                                            <th>Nickname</th>
                                            <th>Email</th>
                                            <th>Phone</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="mc-table-body">
                                        {filteredListers.length > 0 ? (
                                            filteredListers.map((lister) => (
                                                <tr key={lister.id}>
                                                    <td className="fw-semibold">{lister.name || "N/A"}</td>
                                                    <td>{lister.nickname || "N/A"}</td>
                                                    <td>{lister.email || "N/A"}</td>
                                                    <td>{lister.phone || "N/A"}</td>
                                                    <td>
                                                        <span className={`badge ${lister.status ? "bg-success" : "bg-secondary"}`}>
                                                            {lister.status ? "Active" : "Inactive"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="mc-table-action d-flex align-items-center gap-2">
                                                            <button
                                                                type="button"
                                                                className="material-icons border-0 bg-transparent text-info"
                                                                onClick={() => navigate(`/admin/lister/show/${lister.id}`)}
                                                                title="View Details"
                                                            >
                                                                visibility
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="material-icons border-0 bg-transparent text-primary"
                                                                onClick={() => navigate(`/listers/edit/${lister.id}`, { state: { lister } })}
                                                                title="Edit"
                                                            >
                                                                edit
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={6} className="text-center py-4 text-muted">
                                                    {t("No listers found")}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}